import { useForm } from 'react-hook-form'
import { BpButton } from '../../../../shared/components/buttons/bp-button/BpButton'
import { BpInput } from '../../../../shared/components/forms/input/BpInput'
import { EMAIL_REGEX } from '../../../../shared/consts/RegularExpressions.const'
import { BpCheckbox } from '../../../../shared/components/forms/checkbox/BpCheckbox'
import { BpLink } from '../../../../shared/components/text/link/BpLink'
import React, { FormHTMLAttributes } from 'react'
import { GoogleButton } from '../../../../widgets/utility/GoogleButton'
import { useTranslation } from 'react-i18next'
import { validatePassword } from '../../../../shared/lib/helpers/ValidatePassword'
import styles from './SignUpForm.module.scss'

export interface ISignUpFormData {
  scenario?: string
  fullName?: string
  email?: string
  password?: string
  notifyNews?: boolean
}

export interface ISignUpFormProps extends FormHTMLAttributes<HTMLFormElement> {
  submitHandler?: (data: ISignUpFormData) => void
  isLoading?: boolean
}

export const SignUpForm = ({
  submitHandler,
  isLoading = false,
  ...formProps
}: ISignUpFormProps) => {
  const { t } = useTranslation()
  const {
    handleSubmit,
    register,
    formState: { errors, isValid },
  } = useForm<ISignUpFormData>({
    mode: 'onChange',
    defaultValues: {
      fullName: '',
      email: '',
      password: '',
      notifyNews: true,
    },
  })

  return (
    <>
      <h2 className={styles.title}>
        {t('pages.auth.createAccountAndStartYour')}{' '}
        <span className={styles.titleBold}>{t('pages.auth.freeTrial')}</span>
      </h2>
      {
        {
          bluepear: (
            <>
              <GoogleButton />
              <div className={styles.dividerWrap}>
                <div className={styles.dividerText}>or</div>
              </div>
            </>
          ),
          robuscan: null,
          default: null,
        }[process.env.REACT_APP_PROJECT ?? 'default']
      }
      <form
        {...formProps}
        className={styles.form}
        onSubmit={handleSubmit((data: ISignUpFormData) => {
          if (submitHandler) submitHandler(data)
        })}
      >
        <BpInput
          required
          error={errors.fullName?.message}
          fieldSize='lg'
          label={t('pages.auth.fullName')}
          {...register('fullName', {
            required: t('fields.validate.fullNameRequired'),
          })}
        ></BpInput>
        <BpInput
          required
          error={errors.email?.message}
          fieldSize='lg'
          label={t('pages.auth.workEmail')}
          {...register('email', {
            required: t('fields.validate.workEmailRequired'),
            pattern: {
              value: EMAIL_REGEX,
              message: t('fields.validate.enterValidEmail'),
            },
          })}
        ></BpInput>
        <BpInput
          required
          error={errors.password?.message}
          fieldSize='lg'
          label={t('pages.auth.password')}
          type='password'
          {...register('password', {
            required: t('fields.validate.passwordRequired'),
            minLength: {
              value: 8,
              message: t('fields.validate.passwordMustBe'),
            },
            validate: (value) => validatePassword(value, t),
          })}
        ></BpInput>
        <div className={styles.checkboxWrap}>
          <BpCheckbox label={t('pages.auth.receiveNews')} {...register('notifyNews')} />
        </div>
        <BpButton
          className={styles.submitButton}
          disabled={!isValid}
          loading={isLoading}
          size='lg'
          type='submit'
        >
          {t('pages.auth.buttons.continue')}
        </BpButton>
        <div>
          {
            {
              bluepear: (
                <p className={'max-w-[200px]'}>
                  {t('pages.auth.youAgreeTo')}{' '}
                  <a className={'underline'} href={'https://bluepear.net/privacy-policy'}>
                    {t('pages.auth.privacyPolicy')}
                  </a>
                  ,{' '}
                  <a className={'underline'} href={'https://bluepear.net/terms-of-use'}>
                    {t('pages.auth.termsOfUse')}
                  </a>
                  .
                </p>
              ),
              robuscan: (
                <p>
                  {t('pages.auth.youAgreeTo')}{' '}
                  <a className={'underline'} href={'https://robuscan.ru/privacy_policy'}>
                    {t('pages.auth.privacyPolicy')}
                  </a>
                  .
                </p>
              ),
              default: null,
            }[process.env.REACT_APP_PROJECT ?? 'default']
          }
          <p>
            {t('pages.auth.alreadyHaveAccount')}{' '}
            <BpLink to={'/auth'}>{t('pages.auth.logIn')}</BpLink>
          </p>
        </div>
      </form>
    </>
  )
}
